"use client";

import Image from "next/image";
import { useTranslations } from "next-intl";

const IMAGE_BASE = "/image/new-version/molding/injection-mold-tooling/materials";
const DEFAULT_IMAGES = [`${IMAGE_BASE}/1.webp`, `${IMAGE_BASE}/2.webp`, `${IMAGE_BASE}/3.webp`];

const MATERIAL_KEYS = ["toolSteel", "stainless", "aluminum"] as const;

export interface MaterialsForInjectionMoldProps {
	/** 翻译命名空间，需包含 title、各材料的 titleKey/descKey/gradesKey、commonGradesLabel、imageAlt1～3 */
	translationNamespace: string;
	/** 三张图片路径，不传则用 Materials for Injection Mold/1～3.webp */
	imageUrls?: string[];
}

/**
 * Materials for Injection Mold：居中标题，三列网格（每列：标题+图片+描述+Common Grades），带边框
 */
export default function MaterialsForInjectionMold({
	translationNamespace,
	imageUrls = DEFAULT_IMAGES,
}: MaterialsForInjectionMoldProps) {
	const t = useTranslations(translationNamespace);

	return (
		<section className="w-full bg-white py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				{/* 居中标题 */}
				<h2 className="mb-6 text-center text-2xl font-bold leading-tight text-black sm:mb-8 sm:text-[30px] md:mb-10 md:text-4xl lg:mb-12">
					{t("title")}
				</h2>

				{/* 三列网格：每列带边框，内容垂直排列 */}
				<div className="grid grid-cols-1 gap-6 border border-gray-200 bg-white sm:grid-cols-3 sm:gap-0 sm:divide-x sm:divide-gray-200">
					{MATERIAL_KEYS.map((key, index) => (
						<div key={key} className="flex flex-col p-5 sm:p-6 md:p-8">
							{/* 标题 */}
							<h3 className="mb-4 text-left text-lg font-bold text-black sm:text-xl md:mb-5 md:text-2xl">
								{t(`${key}Title`)}
							</h3>

							{/* 图片：占满列宽，固定高度比例 */}
							<div className="relative mb-4 aspect-[16/9] w-full overflow-hidden rounded bg-[#f5f5f5] sm:mb-5">
								<Image
									src={imageUrls[index] ?? imageUrls[0]}
									alt={index === 0 ? t("imageAlt1") : index === 1 ? t("imageAlt2") : t("imageAlt3")}
									fill
									className="object-fill"
									sizes="(max-width: 640px) 100vw, (max-width: 1024px) 33vw, 33vw"
								/>
							</div>

							{/* 描述文本 */}
							<p className="mb-4 flex-1 text-left text-sm leading-relaxed text-black sm:mb-5 md:text-base">
								{t(`${key}Desc`)}
							</p>

							{/* Common Grades */}
							<div className="text-left">
								<span className="text-sm font-bold text-black md:text-base">{t("commonGradesLabel")} </span>
								<span className="text-sm text-black md:text-base">{t(`${key}Grades`)}</span>
							</div>
						</div>
					))}
				</div>
			</div>
		</section>
	);
}
